import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import MultiSelectSearch from './MultiSelectSearch';
import { useAccount } from '../contexts/AccountContext';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './AddCorporateProfilePage.css';   // acp-* page/card/field/footer classes
import './UniversalApiPage.css';
import { apiErrorMessage } from '../utils/apiError';

// Add or edit one access profile.
//
// A FULL PAGE, not a modal (STYLE.md). It is a form with three parts and a route tree in the middle;
// an overlay could not be linked to, would break the back button, and has nowhere to grow.
//
// SAVED IN ONE ACTION. The name and the whole grant go in a single request, so a failure leaves nothing
// behind. Creating the profile and then filling it would leave a half-made row on any error in the
// second call, and no screen from which to notice.

// What a route governs, summed over its subtree. A module is a branch: /blk's own calls are few and the
// ones that matter hang off /blk/v1. Granting the parent grants the subtree, so "how much is this" has
// to be answered over the subtree too.
const subtreeOf = (routes, rootId) => {
    const out = [];
    const queue = [String(rootId)];
    while (queue.length) {
        const id = queue.shift();
        for (const r of routes) {
            if (String(r.parent_id) === id) { out.push(r); queue.push(String(r.id)); }
        }
    }
    return out;
};

const UapiAccessProfileFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = id && id !== 'new';
    // The chain in the nav bar. Targets are offered within it, because an estate-wide hotel list is
    // thousands of rows and the operator is already working inside one chain.
    const { selectedAccount } = useAccount();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [routes, setRoutes] = useState([]);
    // Per-module calls and the redaction sets choosable on them, fetched when a module is narrowed.
    // `listModuleOperations` is the endpoint the credential grant screen already uses and returns both
    // in one response — a second way to ask the same question would be a second thing to keep in step.
    const [moduleCalls, setModuleCalls] = useState({});   // routeId -> { operations, setsByPayload }
    const [callsLoading, setCallsLoading] = useState({}); // routeId -> bool
    // routeId -> { mode: 'ALL'|'SELECTED', operations: { operationId: redactionSetId|'' } }
    const [granted, setGranted] = useState({});
    // Who this profile governs. Ticks within the nav bar's chain; `targetsElsewhere` holds the rows
    // pointing outside it, carried through the save untouched so editing from one chain cannot quietly
    // detach properties in another.
    const [targetOptions, setTargetOptions] = useState({ account: null, hotels: [], credentials: [] });
    const [targets, setTargets] = useState({ account: false, hotels: [] });
    const [targetsElsewhere, setTargetsElsewhere] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const returnTo = '/universal-api/access-profiles';

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const routesRes = await legacyUapiService.listRoutes();
            setRoutes((routesRes.data || routesRes).routes || []);

            const optsRes = selectedAccount
                ? await legacyUapiService.listAccessProfileTargetOptions(selectedAccount)
                : null;
            const opts = optsRes ? (optsRes.data || optsRes) : { account: null, hotels: [], credentials: [] };
            setTargetOptions(opts);

            if (isEdit) {
                const res = await legacyUapiService.getAccessProfile(id);
                const p = res.data || res;
                setName(p.name || '');
                setDescription(p.description || '');

                // Split the profile's targets into the ones this chain can edit and the ones it cannot
                // see. A target is "elsewhere" when it is not among the options offered here.
                const hotelIds = new Set((opts.hotels || []).map(h => String(h.id)));
                const here = { account: false, hotels: [] };
                const away = [];
                for (const t of (p.targets || [])) {
                    if (t.account_id && String(t.account_id) === String(selectedAccount)) here.account = true;
                    else if (t.hotel_id && hotelIds.has(String(t.hotel_id))) here.hotels.push(String(t.hotel_id));
                    // A CREDENTIAL ROW IS NOT EDITED HERE, EVER — it is an override made on the credential
                    // screen. Carried through the save with the out-of-chain rows so editing the routes
                    // cannot detach a credential somebody deliberately pointed at this profile.
                    else away.push(t);
                }
                setTargets(here);
                setTargetsElsewhere(away);
                const g = {};
                for (const m of (p.modules || [])) {
                    g[String(m.uapi_route_id)] = {
                        mode: m.operation_mode || 'ALL',
                        operations: Object.fromEntries((m.operations || [])
                            .map(o => [String(o.uapi_operation_id), o.redaction_set_id ? String(o.redaction_set_id) : ''])),
                    };
                }
                setGranted(g);
            }
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [id, isEdit, selectedAccount]);

    useEffect(() => { load(); }, [load]);

    // Only top-level routes are grantable, because a grant covers the subtree. Offering a child as well
    // would let somebody tick both and then wonder which one won.
    const modules = useMemo(
        () => routes.filter(r => r.parent_id == null).sort((a, b) => a.path.localeCompare(b.path)),
        [routes]);

    // Counted from the route rows, which already carry a per-route `operation_count`. Fetching every
    // operation in the estate to arrive at the same number would be thousands of rows for a badge.
    const callsUnder = useCallback((routeId) => {
        const self = routes.find(r => String(r.id) === String(routeId));
        return [self, ...subtreeOf(routes, routeId)]
            .reduce((n, r) => n + Number((r && r.operation_count) || 0), 0);
    }, [routes]);

    // Fetched on narrowing, not on load. A profile touches a handful of modules and the estate has
    // fifteen; pulling every module's calls upfront would be most of a megabyte nobody reads.
    const loadCalls = useCallback(async (routeId) => {
        const key = String(routeId);
        if (moduleCalls[key] || callsLoading[key]) return;
        setCallsLoading(prev => ({ ...prev, [key]: true }));
        try {
            const res = await legacyUapiService.listModuleOperations(routeId);
            const d = res.data || res;
            const setsByPayload = {};
            for (const set of (d.redactionSets || [])) {
                (setsByPayload[String(set.payload_id)] = setsByPayload[String(set.payload_id)] || []).push(set);
            }
            setModuleCalls(prev => ({ ...prev, [key]: { operations: d.operations || [], setsByPayload } }));
        } catch (e) {
            setModuleCalls(prev => ({ ...prev, [key]: { operations: [], setsByPayload: {}, error: true } }));
        } finally {
            setCallsLoading(prev => ({ ...prev, [key]: false }));
        }
    }, [moduleCalls, callsLoading]);

    // Anything already granted when an existing profile opens needs its calls straight away, or the
    // chosen ones would render as an empty list and read as a profile that grants nothing.
    useEffect(() => {
        for (const routeId of Object.keys(granted)) loadCalls(routeId);
    }, [granted, loadCalls]);

    const toggleModule = (routeId) => setGranted(prev => {
        const next = { ...prev };
        if (next[String(routeId)]) delete next[String(routeId)];
        else { next[String(routeId)] = { mode: 'ALL', operations: {} }; loadCalls(routeId); }
        return next;
    });

    const setMode = (routeId, mode) => {
        // Both modes show the call list now — SELECTED to choose calls, ALL to name a redaction on one.
        loadCalls(routeId);
        setGranted(prev => ({
            ...prev,
            [String(routeId)]: { ...(prev[String(routeId)] || { operations: {} }), mode },
        }));
    };

    const toggleOperation = (routeId, opId) => setGranted(prev => {
        const g = prev[String(routeId)] || { mode: 'SELECTED', operations: {} };
        const ops = { ...g.operations };
        if (String(opId) in ops) delete ops[String(opId)];
        else ops[String(opId)] = '';
        return { ...prev, [String(routeId)]: { ...g, operations: ops } };
    });

    const setRedaction = (routeId, opId, setId) => setGranted(prev => {
        const g = prev[String(routeId)];
        return { ...prev, [String(routeId)]: { ...g, operations: { ...g.operations, [String(opId)]: setId } } };
    });

    // A target already held by ANOTHER profile. Shown as taken rather than offered, because the save
    // would be refused and the operator would have learned it the hard way.
    // Stable across renders, because the option lists below are memoised on it and a chain can hold
    // hundreds of rows — a new function each render would rebuild all of them on every keystroke.
    const takenBy = useCallback((row) => (row && row.profile_id && String(row.profile_id) !== String(id)
        ? row.profile_name : null), [id]);

    // WHAT THE PICKERS OFFER. A chain holds hundreds of properties, so both lists are searchable and
    // neither is rendered in full — a column of 200 checkboxes is not a list anybody reads.
    //
    // A target already held by ANOTHER profile is left OUT of the options rather than shown disabled:
    // the picker has no disabled state, and an option that refuses to be chosen teaches nothing. They
    // are named underneath instead, where there is room to say which profile has them.
    const hotelOptions = useMemo(() => (targetOptions.hotels || [])
        .filter(h => !takenBy(h))
        .map(h => ({ value: String(h.id), label: h.name })), [targetOptions.hotels, takenBy]);

    const takenHotels = useMemo(() => (targetOptions.hotels || [])
        .filter(h => takenBy(h)), [targetOptions.hotels, takenBy]);


    const save = async () => {
        if (!name.trim()) { setError('Give the profile a name.'); return; }
        setSaving(true);
        setError(null);
        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            // Ticks here, plus whatever this profile already pointed at outside this chain.
            targets: [
                ...(targets.account && selectedAccount ? [{ accountId: Number(selectedAccount) }] : []),
                ...targets.hotels.map(k => ({ hotelId: Number(k) })),
                // No credential rows are written from here. A credential that needs a different profile
                // from its hotel's is overridden ON THE CREDENTIAL, which is the screen that shows what
                // that credential can already do — see UapiAccessDetailPage.
                ...targetsElsewhere.map(t => (t.account_id ? { accountId: t.account_id }
                    : t.hotel_id ? { hotelId: t.hotel_id } : { credentialId: t.credential_id })),
            ],
            modules: Object.entries(granted).map(([routeId, g]) => ({
                routeId: Number(routeId),
                mode: g.mode,
                // SELECTED sends every chosen call, with or without a redaction — the row IS the grant.
                // ALL sends only the calls a redaction was named on, because there the row is only the
                // redaction and one without a set would say nothing.
                operations: Object.entries(g.operations)
                    .filter(([, setId]) => g.mode === 'SELECTED' || setId)
                    .map(([opId, setId]) => ({
                        id: Number(opId),
                        redactionSetId: setId ? Number(setId) : null,
                    })),
            })),
        };
        try {
            if (isEdit) await legacyUapiService.updateAccessProfile(id, payload);
            else await legacyUapiService.createAccessProfile(payload);
            navigate(returnTo);
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to save the profile'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">{isEdit ? 'Edit access profile' : 'New access profile'}</h1>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    {loading ? (
                        <div className="loading-section"><Spinner size="md" /><p>Loading routes…</p></div>
                    ) : (<>
                        <div className="acp-field">
                            <label className="acp-label">Name <span className="acp-required">*</span></label>
                            <input
                                className="acp-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Channel manager"
                                autoFocus
                            />
                            <span className="lu-flow-note">
                                What this profile is for. Every screen that offers a profile shows this.
                            </span>
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Description</label>
                            <input
                                className="acp-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Optional — who gets this and why"
                            />
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Applies to</label>
                            <span className="lu-flow-note" style={{ marginBottom: '0.6rem', display: 'block' }}>
                                Who follows this profile. A target POINTS at it — edit the routes below and
                                every property here changes with them. A chain or its hotels; one credential
                                that needs something different is overridden on the credential itself.
                            </span>

                            {!selectedAccount ? (
                                <p className="lu-muted" style={{ margin: 0 }}>
                                    Choose a chain in the navigation bar to point this profile at properties.
                                    The routes below can be set without one.
                                </p>
                            ) : (<>
                                {/* THE CHAIN. Covers every property in it, including ones added later,
                                    which is the reason to target a chain rather than tick its hotels. */}
                                <div className={`uapi-profile-route${targets.account ? ' uapi-profile-route-on' : ''}`}>
                                    <label className="uapi-module-check">
                                        <input
                                            type="checkbox"
                                            checked={!!targets.account}
                                            disabled={!!takenBy(targetOptions.account)}
                                            onChange={() => setTargets(prev => ({ ...prev, account: !prev.account }))}
                                        />
                                        <span className="uapi-module-path">
                                            Everything in {targetOptions.account?.name || 'this chain'}
                                        </span>
                                    </label>
                                    {takenBy(targetOptions.account) && (
                                        <p className="lu-muted" style={{ margin: '0.3rem 0 0 1.5rem', fontSize: '0.75rem' }}>
                                            Already governed by “{takenBy(targetOptions.account)}”.
                                        </p>
                                    )}
                                </div>

                                {/* THE HOTELS. A searchable picker, not a column of checkboxes: a chain
                                    can hold two hundred properties and a list that long is scrolled
                                    past, not read. Chips show what is chosen without opening anything. */}
                                <label className="acp-label" style={{ marginTop: '0.8rem', display: 'block' }}>
                                    Hotels
                                </label>
                                <MultiSelectSearch
                                    options={hotelOptions}
                                    value={targets.hotels}
                                    placeholder={targets.account ? 'Covered by the chain' : 'Search hotels…'}
                                    disabled={!!targets.account}
                                    onChange={(hotels) => setTargets(prev => ({ ...prev, hotels }))}
                                />
                                <span className="lu-flow-note">
                                    A hotel covers every Universal API credential it holds, including ones
                                    issued later.
                                </span>

                                {/* Taken by another profile, and so not offered above. Named here, where
                                    there is room to say which profile has them. */}
                                {takenHotels.length > 0 && (
                                    <p className="lu-flow-note" style={{ marginTop: '0.4rem' }}>
                                        {takenHotels.length} hotel{takenHotels.length === 1 ? '' : 's'} in this
                                        chain already follow another profile and are not offered:{' '}
                                        {takenHotels.map(h => `${h.name} (${h.profile_name})`).join(', ')}.
                                    </p>
                                )}

                                {(targetOptions.hotels || []).length === 0 && (
                                    <p className="lu-muted" style={{ margin: '0.5rem 0 0' }}>
                                        This chain has no hotels to target.
                                    </p>
                                )}

                                {/* Targets in other chains. Read-only here and kept on save — this screen
                                    cannot show what it did not load, and dropping them silently would
                                    detach properties nobody in this chain can see. */}
                                {targetsElsewhere.length > 0 && (
                                    <p className="lu-flow-note" style={{ marginTop: '0.6rem' }}>
                                        This profile also governs {targetsElsewhere.length} target
                                        {targetsElsewhere.length === 1 ? '' : 's'} not editable here —
                                        credentials overridden on their own screen, and anything outside this
                                        chain ({targetsElsewhere.map(t => (t.credential_id
                                            ? `${t.credential_hotel_name || ''} ${t.credential_name || ''}`.trim()
                                            : t.hotel_name || t.account_name || `#${t.id}`)).join(', ')}).
                                        They are kept as they are.
                                    </p>
                                )}
                            </>)}
                        </div>

                        <div className="acp-field">
                            <label className="acp-label">Routes</label>
                            <span className="lu-flow-note" style={{ marginBottom: '0.6rem', display: 'block' }}>
                                Tick a module to grant it. Granted in full it covers every call under it,
                                including ones a later spec import adds; narrowed, it covers only the calls
                                you tick. Either way you can name what a caller is shown of each answer.
                            </span>

                            <div className="uapi-profile-routes">
                                {modules.map(r => {
                                    const g = granted[String(r.id)];
                                    const calls = callsUnder(r.id);
                                    return (
                                        <div key={r.id} className={`uapi-profile-route${g ? ' uapi-profile-route-on' : ''}`}>
                                            <label className="uapi-module-check">
                                                <input
                                                    type="checkbox"
                                                    checked={!!g}
                                                    onChange={() => toggleModule(r.id)}
                                                />
                                                <span className="uapi-module-path">
                                                    {r.path}
                                                    <span className="uapi-module-calls-count">
                                                        {calls} call{calls === 1 ? '' : 's'}
                                                    </span>
                                                </span>
                                            </label>

                                            {g && (
                                                <div className="uapi-profile-mode">
                                                    {['ALL', 'SELECTED'].map(m => (
                                                        <label key={m} className="uapi-profile-mode-opt">
                                                            <input
                                                                type="radio"
                                                                name={`mode-${r.id}`}
                                                                checked={g.mode === m}
                                                                onChange={() => setMode(r.id, m)}
                                                            />
                                                            {m === 'ALL' ? 'Every call' : 'Chosen calls'}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}

                                            {/* The calls, and the redaction each is served with. Shown in BOTH
                                                modes: under "Chosen calls" the tick is what grants the call,
                                                under "Every call" they are all granted already and the only
                                                decision left is what the caller is shown of each answer.
                                                Those are different questions and neither should require
                                                answering the other. */}
                                            {g && (
                                                <div className="uapi-profile-calls">
                                                    {callsLoading[String(r.id)] && (
                                                        <p className="lu-muted" style={{ margin: 0 }}>Loading calls…</p>
                                                    )}
                                                    {(moduleCalls[String(r.id)]?.operations || []).map(o => {
                                                        const on = g.mode === 'ALL' || String(o.id) in g.operations;
                                                        const sets = moduleCalls[String(r.id)]?.setsByPayload?.[String(o.payload_id)] || [];
                                                        return (
                                                            <div key={o.id} className="uapi-profile-call">
                                                                <label className="uapi-module-check">
                                                                    {/* No tick under "Every call": the module
                                                                        granted it, and a checkbox that cannot
                                                                        be unticked only invites the click. */}
                                                                    {g.mode === 'SELECTED' && (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={on}
                                                                            onChange={() => toggleOperation(r.id, o.id)}
                                                                        />
                                                                    )}
                                                                    <span className={`lu-method lu-method-${String(o.method).toLowerCase()}`}>
                                                                        {o.method}
                                                                    </span>
                                                                    <code className="lu-mono">{o.full_path || o.path}</code>
                                                                </label>

                                                                {on && (sets.length > 0 ? (
                                                                    <div className="uapi-calls-redaction">
                                                                        <span className="lu-muted">Sees</span>
                                                                        <select
                                                                            value={g.operations[String(o.id)] || ''}
                                                                            onChange={(e) => setRedaction(r.id, o.id, e.target.value)}
                                                                        >
                                                                            {/* No choice is a real answer: the
                                                                                redaction addressed to whoever
                                                                                is calling applies instead. */}
                                                                            <option value="">
                                                                                Whatever is addressed to this consumer (default)
                                                                            </option>
                                                                            {sets.map(set => (
                                                                                <option key={set.id} value={String(set.id)}>
                                                                                    {set.name}
                                                                                    {set.is_default === 'Y'
                                                                                        ? ' · redacts nothing'
                                                                                        : ` · ${set.rule_count} rule${Number(set.rule_count) === 1 ? '' : 's'}`}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                        <a
                                                                            className="uapi-calls-new-redaction"
                                                                            href={`/admin/universal-api/payloads/${o.payload_id}/redactions`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            title="Define a new redaction for this call's document"
                                                                        >
                                                                            New…
                                                                        </a>
                                                                    </div>
                                                                ) : (
                                                                    <div className="uapi-calls-redaction">
                                                                        <span className="lu-muted">
                                                                            {o.payload_id
                                                                                ? 'No redactions defined for this call’s document yet'
                                                                                : 'No document mapped to this call, so there is nothing to redact'}
                                                                        </span>
                                                                        {o.payload_id && (
                                                                            <a
                                                                                className="uapi-calls-new-redaction"
                                                                                href={`/admin/universal-api/payloads/${o.payload_id}/redactions`}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                            >
                                                                                Define one…
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>)}
                </div>

                {error && (
                    <p className="ppw-error-msg" style={{ margin: '0 2rem 1rem' }}>
                        <AlertCircle size={13} /> {error}
                    </p>
                )}

                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(returnTo)} disabled={saving}>
                        Cancel
                    </button>
                    <Button variant="success" onClick={save} disabled={saving || loading}>
                        {saving ? 'Saving…' : (isEdit ? 'Save profile' : 'Create profile')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UapiAccessProfileFormPage;
